import { apiFetch } from '@shared/auth';
import type { DietSettings } from '@diet/types';

/*
 * 設定的資料存取邊界。
 *
 * 與 diet-repository 分開：entries 與 settings 是兩個資源，各自有自己的路徑、
 * 請求格式與快取時機，混在同一支只會讓「改紀錄的欄位」與「改設定的欄位」互相干擾。
 *
 * 這裡目前不需要 entries 那樣的大小寫翻譯——設定的欄位兩邊同名同型，因此轉換函式
 * 只做「後端可能少給欄位」的補值。
 */

const SETTINGS_PATH = '/api/diet/settings';

/** 後端回傳的設定格式。未設定的欄位會缺席（而非給 0），故型別為 optional。 */
interface DietSettingsPayload {
  dailyCalorieGoal?: number | null;
}

/**
 * 取設定。
 *
 * 從未設定過的使用者，後端回的是 200 與空欄位而非 404，因此這裡沒有「查無」分支——
 * 「還沒設定過」對使用者而言是正常狀態，不該在前端變成一個要處理的錯誤。
 */
export async function fetchSettings(): Promise<DietSettings> {
  const payload = await apiFetch<DietSettingsPayload>(SETTINGS_PATH);
  return toDietSettings(payload);
}

/**
 * 寫入整份設定。
 *
 * 後端為 PUT 取代語意：未帶的欄位會被清成 null，故一律送出完整的設定物件，
 * 不做部分更新。
 */
export async function saveSettings(settings: DietSettings): Promise<DietSettings> {
  const payload = await apiFetch<DietSettingsPayload>(SETTINGS_PATH, {
    method: 'PUT',
    data: settings,
  });
  /* 以後端回傳的內容為準，不用送出的值頂替——它才是實際存下來的樣貌 */
  return toDietSettings(payload);
}

function toDietSettings(payload: DietSettingsPayload): DietSettings {
  return {
    dailyCalorieGoal: payload.dailyCalorieGoal ?? null,
  };
}
